import { analytics } from './analytics';
import { adoptServerDecision, getAnalyticsConsentForServer } from './consent';

type OAuthProvider = 'google' | 'facebook' | 'apple';
type LastLoginMethod = OAuthProvider | 'email';

const LAST_LOGIN_METHODS: readonly LastLoginMethod[] = ['google', 'facebook', 'apple', 'email'];

interface AuthUser {
  id: string;
  email: string;
  name: string;
  provider: OAuthProvider | 'email';
  avatar_url?: string;
  // Absent on responses from an API that predates the consent column; left
  // undefined rather than defaulted, so nothing is reconciled from a guess.
  analytics_consent?: boolean;
}

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'bearer';
  expires_in: number; // seconds until expiration
  user: AuthUser;
}

interface AuthError {
  detail: string;
}

interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  subscribe_to_updates?: boolean;
}

interface RegisterResponse {
  message: string;
  verification_token: string;
  email: string;
}

interface VerifyEmailRequest {
  token: string;
}

interface VerifyEmailResponse {
  message: string;
  user: AuthUser;
}

interface LoginRequest {
  email: string;
  password: string;
}

interface ResendVerificationRequest {
  email: string;
}

interface ForgotPasswordRequest {
  email: string;
}

interface ForgotPasswordResponse {
  message: string;
}

interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

interface ResetPasswordResponse {
  message: string;
  user: AuthUser;
}

interface ValidateResetTokenResponse {
  valid: boolean;
  email: string;
  message: string;
}

/** Stored tier values — 'scholar' is a pricing-page name, never stored. */
type TierValue = 'free' | 'member' | 'premium';

/** Lifecycle of the paid subscription; null means nothing paid is behind it. */
type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'expired' | null;

interface Entitlements {
  /** Effective tier — what they can use now. */
  tier: TierValue;
  /** Display name for `tier`, e.g. 'Scholar' for stored 'premium'. */
  tier_name: string;
  /** What they bought; differs from `tier` once a subscription lapses. */
  stored_tier: TierValue;
  status: SubscriptionStatus;
  provider: 'stripe' | 'apple' | null;
  current_period_end: string | null;
  /** null means unlimited. */
  limit_minutes: number | null;
  used_minutes: number;
  remaining_minutes: number | null;
  /** What the allowance is measured over. Free is 'lifetime' today. */
  window: 'month' | 'lifetime';
  window_start: string | null;
}

class AuthService {
  private baseUrl: string;
  private tokenKey = 'aleph_access_token';
  private refreshTokenKey = 'aleph_refresh_token';
  private tokenExpiryKey = 'aleph_token_expiry';
  private userKey = 'aleph_user';
  private lastLoginMethodKey = 'aleph_last_login_method';
  private refreshTimer: NodeJS.Timeout | null = null;
  // The one refresh in flight, shared by every caller that finds the token
  // expiring at the same moment. Refresh tokens are single-use, so two
  // concurrent refreshes are not redundant, they are fatal: the first wins and
  // the server answers 401 to the second, which this service reads as a
  // revoked session and clears. Seen on 2026-10-13 when a launch delivered
  // six held App Store transactions at once and eight refreshes raced, and
  // behind the reports of readers signed out after leaving the app idle.
  private refreshInFlight: Promise<void> | null = null;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  }

  async initiateOAuthLogin(provider: OAuthProvider): Promise<string> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/login/${provider}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`OAuth initiation failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.authorization_url || !data.authorization_url.url) {
        throw new Error('No authorization URL received from server');
      }

      return data.authorization_url.url;
    } catch (error) {
      console.error('OAuth initiation error:', error);
      throw error;
    }
  }

  /**
   * Verify a native Sign in with Apple identity token and start an Aleph
   * session. The in-app sheet (see native/socialAuth) yields the identity JWT
   * plus, on first authorization, the user's name; the backend verifies the
   * token against Apple's JWKS and returns the standard auth payload.
   */
  async nativeAppleSignIn(payload: {
    identity_token: string;
    given_name?: string;
    family_name?: string;
    nonce?: string;
  }): Promise<AuthResponse> {
    return this.completeNativeSignIn(`${this.baseUrl}/api/auth/callback/apple/native`, payload);
  }

  /**
   * Verify a native Google Sign-In id_token and start an Aleph session. The
   * backend verifies the token against Google's JWKS (audience = the iOS OAuth
   * client ID) and returns the standard auth payload.
   */
  async nativeGoogleSignIn(payload: { id_token: string }): Promise<AuthResponse> {
    return this.completeNativeSignIn(`${this.baseUrl}/api/auth/callback/google/native`, payload);
  }

  // Shared POST + token-storage path for the native /callback/{provider}/native
  // endpoints, which return the same JSON shape as loginWithPassword.
  private async completeNativeSignIn(url: string, body: Record<string, unknown>): Promise<AuthResponse> {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...body,
          analytics_consent: getAnalyticsConsentForServer(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Sign-in failed' }));
        throw new Error(this.extractErrorMessage(errorData.detail) || `Sign-in failed: ${response.statusText}`);
      }

      const authData: AuthResponse = await response.json();

      this.storeAuthData(authData);
      this.scheduleTokenRefresh(authData.expires_in);

      return authData;
    } catch (error) {
      console.error('Native sign-in error:', error);
      throw error;
    }
  }

  // FastAPI error bodies vary: `detail` is a string for HTTPException, but a
  // list of {msg, loc, ...} for 422 validation errors. Coerce either into a
  // readable string so callers never surface "[object Object]".
  private extractErrorMessage(detail: unknown): string {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((item) => (item && typeof item === 'object' && 'msg' in item ? String((item as { msg: unknown }).msg) : String(item)))
        .filter(Boolean)
        .join('; ');
    }
    return '';
  }

  async registerWithEmail(registerData: RegisterRequest): Promise<RegisterResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...registerData,
          analytics_consent: getAnalyticsConsentForServer(),
        }),
      });

      if (!response.ok) {
        const errorData: AuthError = await response.json().catch(() => ({ detail: 'Registration failed' }));
        throw new Error(errorData.detail || `Registration failed: ${response.statusText}`);
      }

      const registerResponse: RegisterResponse = await response.json();
      return registerResponse;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  async verifyEmail(verifyData: VerifyEmailRequest): Promise<VerifyEmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(verifyData),
      });

      if (!response.ok) {
        const errorData: AuthError = await response.json().catch(() => ({ detail: 'Email verification failed' }));
        throw new Error(errorData.detail || `Email verification failed: ${response.statusText}`);
      }

      const verifyResponse: VerifyEmailResponse = await response.json();
      return verifyResponse;
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  }

  async loginWithPassword(loginData: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...loginData,
          analytics_consent: getAnalyticsConsentForServer(),
        }),
      });

      if (!response.ok) {
        const errorData: AuthError = await response.json().catch(() => ({ detail: 'Login failed' }));
        throw new Error(errorData.detail || `Login failed: ${response.statusText}`);
      }

      const authData: AuthResponse = await response.json();

      this.storeAuthData(authData);
      this.scheduleTokenRefresh(authData.expires_in);

      return authData;
    } catch (error) {
      console.error('Password login error:', error);
      throw error;
    }
  }

  async resendVerification(resendData: ResendVerificationRequest): Promise<{ message: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resendData),
      });

      if (!response.ok) {
        const errorData: AuthError = await response.json().catch(() => ({ detail: 'Resend verification failed' }));
        throw new Error(errorData.detail || `Resend verification failed: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Resend verification error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      const token = this.getAccessToken();

      if (token) {
        await fetch(`${this.baseUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAuthData();
    }
  }

  async deleteAccount(): Promise<void> {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseUrl}/api/auth/user`, {
        method: 'DELETE',
      });

      if (!response.ok && response.status !== 204) {
        const errorData: AuthError = await response.json().catch(() => ({ detail: 'Account deletion failed' }));
        throw new Error(errorData.detail || `Account deletion failed: ${response.statusText}`);
      }

      this.clearAuthData();
    } catch (error) {
      console.error('Account deletion error:', error);
      throw error;
    }
  }

  /**
   * The signed-in user, verified against the server when it can be reached.
   *
   * Only an explicit 401 ends the session. Every other outcome - a 5xx, a
   * malformed body, or a request that never reached a server at all - falls
   * back to the stored user and leaves credentials untouched, because none of
   * them is the server saying this session is invalid.
   *
   * That distinction is the difference between a flaky network and a sign-out.
   * Clearing on any failure meant an offline cold start destroyed the refresh
   * token, and getting it back needs the very network that just failed. A
   * revoked account is still caught: makeAuthenticatedRequest() clears on a
   * genuine 401 at the first round trip that succeeds.
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    if (!this.getAccessToken()) {
      return null;
    }

    try {
      // Through makeAuthenticatedRequest so an access token that merely aged
      // out is refreshed rather than read as a rejection. A 401 that still
      // comes back has survived a successful refresh, which is the server
      // genuinely refusing the account.
      const response = await this.makeAuthenticatedRequest(`${this.baseUrl}/api/auth/user`, {
        method: 'GET',
      });

      if (response.status === 401) {
        this.clearAuthData();
        return null;
      }

      if (!response.ok) {
        console.warn(
          `[auth] Could not verify the session (${response.status}); keeping stored credentials.`,
        );
        return this.getStoredUser();
      }

      const user: AuthUser = await response.json();
      this.reconcileAnalyticsConsent(user);
      return user;
    } catch (error) {
      // Either the request never reached a server (offline, DNS, TLS, abort),
      // in which case the stored session is assumed good rather than thrown
      // away unverified, or the refresh token was rejected, in which case
      // refreshAccessToken() has already cleared storage and this is null.
      console.warn('[auth] Session could not be verified; keeping stored credentials.', error);
      return this.getStoredUser();
    }
  }

  /**
   * Exchange the refresh token for a new access/refresh pair. Concurrent
   * callers share one request; see refreshInFlight for why that matters.
   */
  refreshAccessToken(): Promise<void> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refreshAccessTokenOnce().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async refreshAccessTokenOnce(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken();

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        // Only the server rejecting the refresh token ends the session. The
        // endpoint answers 401 for every genuine rejection - bad token type,
        // missing or expired session row, unknown user, malformed JWT - so
        // anything else is an outage, not a revocation.
        if (response.status === 401) {
          // Another tab shares this storage and may have rotated the token
          // while this request was in flight. The server then rejects the one
          // sent here, but the session lives on under the newer token.
          const current = this.getRefreshToken();
          if (current && current !== refreshToken) {
            const expiry = this.getTokenExpiry();
            if (expiry) {
              this.scheduleTokenRefresh(Math.max(0, Math.floor((expiry - Date.now()) / 1000)));
            }
            return;
          }
          this.clearAuthData();
          throw new Error('Refresh token expired');
        }
        throw new Error(`Token refresh failed: ${response.statusText}`);
      }

      const authData: AuthResponse = await response.json();

      // Store new tokens
      this.storeTokenData(authData);

      // Schedule next refresh
      this.scheduleTokenRefresh(authData.expires_in);
    } catch (error) {
      // Rethrow without clearing: the 401 branch above has already cleared if
      // the session was genuinely rejected, and every other path here is a
      // transport failure or an outage that must leave the refresh token in
      // place for the next attempt.
      console.warn('[auth] Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * What the signed-in user's plan grants and how much of it is left.
   *
   * `tier` is the *effective* tier — what they can use right now — which is
   * not the same as what they bought once a subscription lapses. `window`
   * says what the allowance is measured over ('month' or 'lifetime'), so the
   * copy can say "this month" only when that is actually true.
   */
  async getEntitlements(): Promise<Entitlements> {
    const response = await this.makeAuthenticatedRequest(`${this.baseUrl}/api/auth/entitlements`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch entitlements: ${response.statusText}`);
    }

    return response.json();
  }

  async getUserUsageHistory() {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.baseUrl}/api/auth/usage/history`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch usage history: ${response.statusText}`);
      }

      const data = await response.json();
      return data.sessions || [];
    } catch (error) {
      console.error('Get usage history error:', error);
      throw error;
    }
  }

  async forgotPassword(forgotData: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(forgotData),
      });

      if (!response.ok) {
        const errorData: AuthError = await response.json().catch(() => ({ detail: 'Password reset request failed' }));
        throw new Error(errorData.detail || `Password reset request failed: ${response.statusText}`);
      }

      const result: ForgotPasswordResponse = await response.json();
      return result;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  async validateResetToken(token: string): Promise<ValidateResetTokenResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/reset-password/validate/${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData: AuthError = await response.json().catch(() => ({ detail: 'Token validation failed' }));
        throw new Error(errorData.detail || `Token validation failed: ${response.statusText}`);
      }

      const result: ValidateResetTokenResponse = await response.json();
      return result;
    } catch (error) {
      console.error('Validate reset token error:', error);
      throw error;
    }
  }

  async resetPassword(resetData: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resetData),
      });

      if (!response.ok) {
        const errorData: AuthError = await response.json().catch(() => ({ detail: 'Password reset failed' }));
        throw new Error(errorData.detail || `Password reset failed: ${response.statusText}`);
      }

      const result: ResetPasswordResponse = await response.json();
      return result;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  /**
   * Bring this browser in line with the decision stored on the account, which
   * is the copy the API gates on and the only one that follows the user across
   * devices. Matters most for the grandfathered: their browser assumes consent
   * from the presence of a session, so if they have since turned it off
   * elsewhere, only this stops the local analytics.
   */
  private reconcileAnalyticsConsent(user: AuthUser): void {
    if (typeof user.analytics_consent === 'boolean') {
      adoptServerDecision(user.analytics_consent);
    }
  }

  private storeAuthData(authData: AuthResponse): void {
    try {
      this.storeTokenData(authData);
      localStorage.setItem(this.userKey, JSON.stringify(authData.user));
      this.reconcileAnalyticsConsent(authData.user);
    } catch (error) {
      console.error('Failed to store auth data:', error);
    }
  }

  private storeTokenData(authData: AuthResponse): void {
    try {
      const expiryTime = Date.now() + (authData.expires_in * 1000);
      localStorage.setItem(this.tokenKey, authData.access_token);
      localStorage.setItem(this.refreshTokenKey, authData.refresh_token);
      localStorage.setItem(this.tokenExpiryKey, expiryTime.toString());
    } catch (error) {
      console.error('Failed to store token data:', error);
    }
  }

  private clearAuthData(): void {
    try {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.refreshTokenKey);
      localStorage.removeItem(this.tokenExpiryKey);
      localStorage.removeItem(this.userKey);
      analytics.reset();
    } catch (error) {
      console.error('Failed to clear auth data:', error);
    }
  }

  getAccessToken(): string | null {
    try {
      return localStorage.getItem(this.tokenKey);
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }

  getRefreshToken(): string | null {
    try {
      return localStorage.getItem(this.refreshTokenKey);
    } catch (error) {
      console.error('Failed to get refresh token:', error);
      return null;
    }
  }

  getTokenExpiry(): number | null {
    try {
      const expiry = localStorage.getItem(this.tokenExpiryKey);
      return expiry ? parseInt(expiry) : null;
    } catch (error) {
      console.error('Failed to get token expiry:', error);
      return null;
    }
  }

  getStoredUser(): AuthUser | null {
    try {
      const userJson = localStorage.getItem(this.userKey);
      return userJson ? JSON.parse(userJson) : null;
    } catch (error) {
      console.error('Failed to get stored user:', error);
      return null;
    }
  }

  getLastLoginMethod(): LastLoginMethod | null {
    try {
      const method = localStorage.getItem(this.lastLoginMethodKey);
      return LAST_LOGIN_METHODS.includes(method as LastLoginMethod)
        ? method as LastLoginMethod
        : null;
    } catch (error) {
      console.error('Failed to get last login method:', error);
      return null;
    }
  }

  setLastLoginMethod(method: LastLoginMethod): void {
    try {
      localStorage.setItem(this.lastLoginMethodKey, method);
    } catch (error) {
      console.error('Failed to store last login method:', error);
    }
  }

  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;
    return Date.now() >= expiry;
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const user = this.getStoredUser();
    return !!(token && user && !this.isTokenExpired());
  }

  /**
   * True when stored credentials exist to ATTEMPT an authenticated request.
   * Unlike isAuthenticated(), this deliberately ignores token expiry:
   * makeAuthenticatedRequest() refreshes expired tokens on demand, so a stale
   * or missing aleph_token_expiry must not silently disable background work
   * (e.g. progress sync) while direct API calls keep succeeding.
   */
  hasStoredCredentials(): boolean {
    return !!(this.getAccessToken() && this.getStoredUser());
  }

  scheduleTokenRefresh(expiresIn: number): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Schedule refresh 5 minutes before expiration
    const refreshDelay = Math.max(0, (expiresIn - 300) * 1000); // 5 minutes before expiry

    this.refreshTimer = setTimeout(async () => {
      try {
        await this.refreshAccessToken();
      } catch (error) {
        console.error('Automatic token refresh failed:', error);
        // Token refresh failed, user will need to re-login on next API call
      }
    }, refreshDelay);
  }

  isTokenExpiringSoon(minutesAhead: number = 5): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;
    const bufferTime = minutesAhead * 60 * 1000; // Convert to milliseconds
    return Date.now() >= (expiry - bufferTime);
  }

  /**
   * Push a changed analytics decision to the server.
   *
   * The API gates its own analytics on the user row, so a withdrawal made in
   * the browser would otherwise leave the backend tracking until the next
   * login. Best effort: a failure here is corrected by the next login, which
   * carries the decision anyway.
   */
  async updateAnalyticsConsent(granted: boolean): Promise<void> {
    if (!this.hasStoredCredentials()) return;
    await this.makeAuthenticatedRequest(`${this.baseUrl}/api/auth/analytics-consent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ analytics_consent: granted }),
    });
  }

  /**
   * The access token, refreshed first if it has expired or is about to, for
   * callers that hand a bearer token to something other than
   * makeAuthenticatedRequest(). A refresh that fails returns whatever is
   * stored: null when the server rejected the session and storage was cleared,
   * the old token when the server could not be reached.
   */
  async getValidAccessToken(): Promise<string | null> {
    if (this.getAccessToken() && this.isTokenExpiringSoon()) {
      try {
        await this.refreshAccessToken();
      } catch (error) {
        console.warn('[auth] Could not refresh before handing out the access token:', error);
      }
    }
    return this.getAccessToken();
  }

  async makeAuthenticatedRequest(url: string, options: RequestInit = {}): Promise<Response> {
    // Check if token needs refresh
    if (this.isTokenExpiringSoon()) {
      try {
        await this.refreshAccessToken();
      } catch (error) {
        console.error('Token refresh failed, proceeding with current token:', error);
      }
    }

    const token = this.getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    const authHeaders = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers: authHeaders,
    });

    if (response.status === 401) {
      // A refresh elsewhere replaces the server's copy of the access token, so
      // a request sent with the old one just before comes back 401. If the
      // token has already moved on, retry with it rather than refreshing again.
      try {
        if (this.getAccessToken() === token) {
          await this.refreshAccessToken();
        }
      } catch (refreshError) {
        // Not cleared here: refreshAccessToken() clears when the server
        // rejected the refresh token, and any other failure is an outage that
        // must leave the session in place.
        console.error('Token refresh on 401 failed:', refreshError);
        throw new Error('Authentication expired');
      }

      const newToken = this.getAccessToken();
      if (!newToken) {
        throw new Error('Authentication expired');
      }

      return fetch(url, {
        ...options,
        headers: { ...authHeaders, 'Authorization': `Bearer ${newToken}` },
      });
    }

    return response;
  }

}

export const authService = new AuthService();
export type {
  OAuthProvider,
  LastLoginMethod,
  AuthUser,
  AuthResponse,
  RegisterRequest,
  RegisterResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  LoginRequest,
  ResendVerificationRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ValidateResetTokenResponse,
  Entitlements,
  TierValue,
  SubscriptionStatus
};
